import { useState, useEffect, useCallback } from "react";
import { galleryPhotos } from "@/mocks/gallery";

const VISIBLE = 3;

export default function Gallery() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = galleryPhotos.length;

  const goTo = useCallback((index: number) => {
    setCurrent(((index % count) + count) % count);
  }, [count]);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % count);
  }, [count]);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + count) % count);
  }, [count]);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [next, paused]);

  const visiblePhotos = Array.from({ length: VISIBLE }, (_, i) =>
    galleryPhotos[(current + i) % count]
  );

  return (
    <section
      id="gallery"
      className="w-full bg-background-50 py-10 md:py-12 lg:py-14"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary-600 mb-4">
            Photo Gallery
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
            Moments &amp; Milestones
          </h2>
        </div>

        {/* Slideshow */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Cards */}
          <div
            key={current}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 animate-gallery-fade"
          >
            {visiblePhotos.map((photo) => (
              <figure
                key={photo.title}
                className="flex flex-col bg-background-100 border border-background-200/70 rounded-lg overflow-hidden"
              >
                <div className="relative w-full aspect-[4/3] overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    title={photo.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="p-4">
                  <h3 className="text-base font-heading font-semibold text-foreground-950">
                    {photo.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                    {photo.description}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Prev / Next arrows */}
          <button
            type="button"
            onClick={prev}
            aria-label="Previous photos"
            className="absolute left-3 top-1/3 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next photos"
            className="absolute right-3 top-1/3 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-lg" />
          </button>

          {/* Dots */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {galleryPhotos.map((item, index) => (
              <button
                key={item.title}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`Go to photo ${index + 1}`}
                className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                  index === current
                    ? "bg-primary-500 w-6"
                    : "bg-background-300 hover:bg-primary-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}